import { createSlice } from "@reduxjs/toolkit";

import { fetchStatus } from "./preferenceSlice";

import {
  onDeleteExistingMapping,
  onSaveEditedMappings,
  onAddShFieldsToCustomMappings,
} from "./mappingSlice";

const initialState = {
  isProductSyncRulesSaved: false,
  msbcProductFilterPreference: undefined,
  shopifyProductFilterPreference: undefined,
  preferenceForSearchParameters: "selectSearchParameters",
  preferenceForProducts: "doNotGenerateInvoice",
  defaultProductPreference: undefined,
  preferenceForCreatingProduct: false,
  defaultProductForCreating: undefined,
};

export const productSlice = createSlice({
  name: "product",
  initialState,
  reducers: {
    onChangePreferenceForSearchParameters(state, { payload }) {
      state.preferenceForSearchParameters = payload.selected;
      state.isProductSyncRulesSaved = false;
    },

    onChangePreferenceForProducts(state, { payload }) {
      state.preferenceForProducts = payload.selected;
      state.isProductSyncRulesSaved = false;
      if (state.preferenceForCreatingProduct) {
        if (
          payload.selected === "doNotGenerateInvoice" ||
          payload.selected === "useDefaultProduct"
        ) {
          state.preferenceForCreatingProduct = false;
        }
      }
    },

    onChangePreferenceForCreatingProduct(state) {
      state.preferenceForCreatingProduct = !state.preferenceForCreatingProduct;
      if (state.preferenceForCreatingProduct === false) {
        state.defaultProductForCreating = undefined;
      }
      state.isProductSyncRulesSaved = false;
    },

    onChangeProductPreference(state, { payload }) {
      if (payload.selectingFor === "shopifyProductFilterDropdown") {
        state.shopifyProductFilterPreference = {
          name: payload.selectedOption.HRF,
          value: payload.selectedOption.CRF,
        };
      } else if (payload.selectingFor === "msbcProductFilterDropdown") {
        state.msbcProductFilterPreference = {
          name: payload.selectedOption.HRF,
          value: payload.selectedOption.CRF,
        };
      } else if (payload.selectingFor === "product") {
        state.defaultProductForCreating = {
          name: payload.selectedOption.HRF,
          value: payload.selectedOption.CRF,
        };
      } else if (payload.selectingFor === "defaultProduct") {
        state.defaultProductPreference = {
          name: payload.selectedOption.HRF,
          value: payload.selectedOption.CRF,
        };
      }
      state.isProductSyncRulesSaved = false;
    },

    setIsProductSyncRulesSaved(state) {
      state.isProductSyncRulesSaved = true;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchStatus.fulfilled, (state, actions) => {
      const { preference, mapping } = actions.payload;

      if (preference && Object.keys(preference).length > 0) {
        const { product } = preference;

        if (product?.generateInvoice) {
          state.preferenceForProducts =
            product.generateInvoice?.create === true
              ? "useDefaultProduct"
              : "doNotGenerateInvoice";

          state.defaultProductPreference = product.generateInvoice?.mapping
            ?.msbcDefaultProduct?.value
            ? product.generateInvoice.mapping.msbcDefaultProduct
            : undefined;
        }

        if (product?.filterOptions) {
          state.shopifyProductFilterPreference = product.filterOptions
            ?.filterShProperty?.value
            ? product.filterOptions.filterShProperty
            : undefined;
          state.msbcProductFilterPreference = product.filterOptions
            ?.filterMsbcProperty?.value
            ? product.filterOptions.filterMsbcProperty
            : undefined;
        }

        state.preferenceForSearchParameters =
          product.searchParameters === true
            ? "selectSearchParameters"
            : "setCustomMappings";

        if (product?.createProduct) {
          state.preferenceForProducts = product?.createProduct?.create
            ? "findCreateProduct"
            : product?.generateInvoice?.create
            ? "useDefaultProduct"
            : "doNotGenerateInvoice";
          state.preferenceForCreatingProduct =
            product?.createProduct?.customProduct?.useMsbcDefaultProduct;
          state.defaultProductForCreating = product?.createProduct
            ?.customProduct?.mapping?.msbcDefaultProduct?.value
            ? product.createProduct.customProduct.mapping.msbcDefaultProduct
            : undefined;
        }
      }

      if (
        ((state.preferenceForProducts === "useDefaultProduct" &&
          state.defaultProductPreference) ||
          state.preferenceForProducts === "doNotGenerateInvoice" ||
          (state.preferenceForProducts === "findCreateProduct" &&
            (!state.preferenceForCreatingProduct ||
              (state.preferenceForCreatingProduct &&
                state.defaultProductForCreating)))) &&
        ((state.preferenceForSearchParameters === "selectSearchParameters" &&
          state.shopifyProductFilterPreference &&
          state.msbcProductFilterPreference) ||
          (state.preferenceForSearchParameters === "setCustomMappings" &&
            mapping?.product?.length > 0))
      ) {
        state.isProductSyncRulesSaved = true;
      }
    });

    builder.addCase(onDeleteExistingMapping, (state, actions) => {
      if (
        actions.payload.mappingsFor &&
        actions.payload.mappingsFor === "products"
      ) {
        state.isProductSyncRulesSaved = false;
      }
    });

    builder.addCase(onSaveEditedMappings, (state, actions) => {
      if (
        actions.payload.mappingsFor &&
        actions.payload.mappingsFor === "products"
      ) {
        state.isProductSyncRulesSaved = false;
      }
    });

    builder.addCase(onAddShFieldsToCustomMappings, (state, actions) => {
      if (
        actions.payload.mappingsFor &&
        actions.payload.mappingsFor === "products"
      ) {
        state.isProductSyncRulesSaved = false;
      }
    });
  },
});

export const {
  onChangePreferenceForProducts,
  onChangePreferenceForSearchParameters,
  onChangePreferenceForCreatingProduct,
  onChangeProductPreference,
  setIsProductSyncRulesSaved,
} = productSlice.actions;

export default productSlice.reducer;
